import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2, AlertTriangle, Trash2, AlertCircle } from 'lucide-react'

interface DeleteGroupDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  groupName: string
  // Form state
  confirmText: string
  onConfirmTextChange: (value: string) => void
  // Actions
  onDelete: () => void
  deleting: boolean
  error: string | null
}

export function DeleteGroupDialog({
  open,
  onOpenChange,
  groupName,
  confirmText,
  onConfirmTextChange,
  onDelete,
  deleting,
  error,
}: DeleteGroupDialogProps) {
  const handleClose = () => {
    onConfirmTextChange('')
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Delete Group
          </DialogTitle>
          <DialogDescription>
            This action is permanent and cannot be undone. All data associated with this group will be permanently deleted, including:
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
            <li>All leagues and their configurations</li>
            <li>All seasons and brackets</li>
            <li>All matches and scores</li>
            <li>All member associations</li>
            <li>All pending invites</li>
          </ul>

          <div className="space-y-2">
            <Label htmlFor="delete-confirm" className="text-sm">
              To confirm, type <span className="font-mono font-semibold text-foreground">{groupName}</span> below:
            </Label>
            <Input
              id="delete-confirm"
              value={confirmText}
              onChange={(e) => onConfirmTextChange(e.target.value)}
              placeholder="Type group name to confirm"
              className="font-mono"
            />
          </div>

          {error && (
            <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3 flex items-center gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onDelete}
            disabled={deleting || confirmText !== groupName}
          >
            {deleting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Group Forever
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
